import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";

import { getStructuralPlantProjects } from "./api";
// import { getStructuralPlantProjects } from "../../AllEquipments/Structural/";

import { useStructureModeling } from "./structureModelingContext";

function LoadProjectDataComboBox({
  plantId,
  clientName,
  type,
  onSelect,
  disabled = false,
  size = "small",
  fullWidth = true,
}) {
  const {
    selectedProjectId,
    setSelectedProjectId,
    loadStructuralData,
    initializeAllTables,

  } = useStructureModeling();

  const [loading, setLoading] = useState(false);
  const [loadingSelectedProject, setLoadingSelectedProject] = useState(false);
  const [err, setErr] = useState("");
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);

  const normalizeType = (value) =>
    String(value || "")
      .trim()
      .toLowerCase();

  const normalizedType = useMemo(() => normalizeType(type), [type]);

  const projectLabel = (project) =>
    project?.name || project?.link || "Unnamed";

  const fetchProjects = async () => {
    if (!plantId) {
      setErr("Missing plantId.");
      setProjects([]);
      setSelectedProject(null);
      return;
    }

    if (!clientName) {
      setErr("Missing clientName.");
      setProjects([]);
      setSelectedProject(null);
      return;
    }

    if (!normalizedType) {
      setErr("Missing project type.");
      setProjects([]);
      setSelectedProject(null);
      return;
    }

    setLoading(true);
    setErr("");
    setProjects([]);
    setSelectedProject(null);

    try {
      let res;

      if (normalizedType === "pip") {
       // res = await getStructuralPlantProjects(plantId, clientName);
      } else if (normalizedType === "struc") {
         res = await getStructuralPlantProjects(plantId, clientName);
      } else {
        throw new Error(`Unsupported project type: ${normalizedType}`);
      }

      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : [];

      setProjects(list);

      if (!list.length) {
        setErr("No projects found.");
        return;
      }

      const currentSelected =
        list.find((project) => project?.id === selectedProjectId) || null;

      setSelectedProject(currentSelected);
    } catch (error) {
      setErr(error?.message || "Failed to load projects.");
    } finally {
      setLoading(false);
    }
  };

 
  useEffect(() => {
    fetchProjects();
    initializeAllTables();

  }, [plantId, clientName, normalizedType]);

  useEffect(() => {
    if (!selectedProjectId || !projects.length) {
      return;
    }

    const currentSelected =
      projects.find((project) => project?.id === selectedProjectId) || null;

    setSelectedProject(currentSelected);
  }, [selectedProjectId, projects]);

  const helperText = useMemo(() => {
    if (err) return err;

    if (loadingSelectedProject) {
      return "Loading selected structural data...";
    }

    if (plantId && normalizedType) {
      return `${normalizedType.toUpperCase()} projects`;
    }

    return "";
  }, [err, loadingSelectedProject, plantId, normalizedType]);

  const handleChange = async (_event, project) => {
    setErr("");
    setSelectedProject(project);

    setSelectedProjectId(project.id);
    try {
      setLoadingSelectedProject(true);

      if (normalizedType === "struc") {
        await loadStructuralData(project.id);
      }

      onSelect?.(project);
    } catch (error) {
      setErr(error?.message || "Failed to load selected structural data.");
    } finally {
      setLoadingSelectedProject(false);
    }
  };

  const isDisabled = disabled || loading || loadingSelectedProject;

  return (
    <Box sx={{ width: fullWidth ? "100%" : 360 }}>
      <Autocomplete
        size={size}
        fullWidth={fullWidth}
        disabled={isDisabled}
        loading={loading || loadingSelectedProject}
        options={projects}
        value={selectedProject}
        getOptionLabel={projectLabel}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        onChange={handleChange}
        noOptionsText={err || "No projects found"}
        sx={{
          "& .MuiOutlinedInput-root": {
            minHeight: 20,
            height: 25,
            padding: "0 32px 0 8px !important",
            fontSize: 12,
          },
      
          "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
            padding: "0 !important",
            height: 25,
            lineHeight: "25px",
            fontSize: 12,
          },
      
          "& .MuiInputLabel-root": {
            fontSize: 12,
            transform: "translate(14px, 5px) scale(1)",
          },
      
          "& .MuiInputLabel-shrink": {
            transform: "translate(14px, -9px) scale(0.75)",
          },
      
          "& .MuiAutocomplete-endAdornment": {
            top: "50%",
            transform: "translateY(-50%)",
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            error={Boolean(err)}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading || loadingSelectedProject ? (
                    <CircularProgress size={14} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option?.id || option?.name}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12 }}>
              {projectLabel(option)}
            </Typography>
          </Box>
        )}
      />
    </Box>
  );
}

export default LoadProjectDataComboBox;